import { ContentType, Editor, Field } from 'contensis-core-api';
import clone from 'rfdc';
import { ContentTypeAssertions } from './ContentTypeAssertions';
import { ContentTypeFieldAssertions } from './ContentTypeFieldAssertions';
import ContensisRepository from '~/repositories/ContensisRepository';
import { merge } from '~/util/merge';
import { isArray } from '~/util/typeguards';

type EditorProperties = Required<Editor>['properties'];

/**
 * A series of static helper methods for transforming data within content types or components
 */
export class ContentTypeTransformations {
  /**
   * Replaces TransformLanguageCodes
   */
  static RenameKeysAndValues = <T>(
    obj: T,
    find: string,
    replace: string
  ): T => {
    const keyValues = Object.entries(clone()(obj as any)).map(
      ([key, value]) => {
        const newKey = key === find ? replace : key;
        let newValue = value === find ? replace : value;
        if (
          typeof value === 'object' &&
          value !== null &&
          !Array.isArray(value)
        ) {
          newValue = ContentTypeTransformations.RenameKeysAndValues(
            value,
            find,
            replace
          );
        }
        return [newKey, newValue];
      }
    );
    return Object.fromEntries(keyValues);
  };

  /**
   * Replaces TransformReadOnlyFields
   */
  static SetFieldEditorProperty = <T extends keyof EditorProperties>(
    field: Field,
    property: T,
    value: EditorProperties[T]
  ) => {
    const f = clone()(field);
    if (!f.editor) f.editor = {};
    if (!f.editor.properties) f.editor.properties = {};
    f.editor.properties[property] = value;
    return f;
  };

  // TODO: Remove
  static TransformLanguageCodes = <T>(
    contentTypes: T,
    source: string,
    target: string
  ): T => {
    if (source && target) {
      const str = JSON.stringify(contentTypes).replace(
        new RegExp(source, 'g'),
        target
      );
      return JSON.parse(str);
    }
    return contentTypes;
  };

  // TODO: Remove
  static TransformReadOnlyFields = (fields: Field[] = []) =>
    fields.map((field) =>
      merge(field, { editor: { properties: { readOnly: true } } })
    );

  static CanvasNoRestrictions = () => {
    return {
      allowedTypes: {
        types: [
          {
            type: '*',
          },
          {
            type: '_fragment',
            decorators: {
              allowed: [
                {
                  decorator: '*',
                },
              ],
            },
          },
        ],
      },
    } as Field['validations'];
  };

  static RemoveFieldValidations = (
    field: Field,
    contensis: ContensisRepository
  ) => {
    const assert = ContentTypeFieldAssertions;
    const { validation } = assert;

    // Delete required flag
    if (validation.IsRequired(field)) {
      delete field.validations?.required;
      delete field.validations?.minCount;
      delete field.validations?.maxCount;
    }

    // Delete string length validations
    if (validation.HasMinLength(field) || validation.HasMaxLength(field)) {
      delete field.validations?.minLength;
      delete field.validations?.maxLength;
    }

    // Delete regex validation
    if (validation.HasRegex(field)) {
      delete field.validations?.regex;
    }

    // Delete image validations
    if (assert.IsImage(field)) field.validations = {};

    // if (validation.IsAltTextRequired(field)) {
    //   delete field.validations?.altTextRequired;
    // }

    // if (validation.HasImageDimensions(field)) {
    //   delete field.validations?.imageDimensions;
    // }

    // Set entry content type restrictions to all available content types
    const { context } = contensis;
    if (
      validation.HasAllowedContentTypes(field) &&
      isArray(context.contentTypes) &&
      context.contentTypes.length
    ) {
      const contentTypeIds = context.contentTypes
        .filter(
          (ct) =>
            ContentTypeAssertions.isEntryContentType(ct) &&
            ContentTypeAssertions.isPublished(ct)
        )
        .map((ct) => ct.id);
      const migrateIds = contensis.models.toMigrate
        .filter(
          (m) =>
            ContentTypeAssertions.isEntryContentType(m.resource) &&
            ['create', 'two-pass'].includes(m.status)
        )
        .map((m) => m.id);
      const ids = [...new Set([...contentTypeIds, ...migrateIds])].sort();
      // Include all content types, asset types or form content types
      // in `allowedContentTypes` depending on the field type
      if (assert.IsEntry(field)) {
        field.validations.allowedContentTypes.contentTypes = ids;
      }
      if (assert.IsAsset(field)) {
        // Don't include image asset type unless it already exists here
        const includeImageType =
          field.validations.allowedContentTypes.contentTypes?.includes('image');
        field.validations.allowedContentTypes.contentTypes = context.assetTypes
          .map((ct) => (ct.id !== 'image' || includeImageType ? ct.id : null))
          .filter((id): id is string => !!id)
          .sort();
      }

      if (assert.IsContentTypeResourcePicker(field)) {
        field.validations.allowedIds = { ids };
      }

      if (assert.IsFormResourcePicker(field)) {
        const ids = context.forms.map((ct) => ct.id).sort();
        field.validations.allowedIds = { ids };
      }
    }

    if (assert.IsCanvas(field)) {
      field.validations = ContentTypeTransformations.CanvasNoRestrictions();
    }

    return field;
  };

  /** Transform the returned content types to not show the full fields:[] inside composer fields
   * to match the behaviour in the Contensis 14 UI and to prevent changes in components causing
   * updates in content types that are not needed
   *
   * Filter classic webpage content types as these are not in the scope of this tool
   * Also filter asset dataFormat such as pdf and image because these often contain
   * dependent asset types that won't exist in a new project
   * e.g.: "The field with ID 'thumbnail' has an 'allowedContentTypes' validator with an invalid value 'Image', the content type does not exist in the project 'baseContentTypes'" */
  static SeparateContentTypesAndFilterComponentFields = (
    contentTypes: ContentType[]
  ) => {
    const entryContentTypes = [];
    const assetTypes = [];
    const formContentTypes = [];
    const webpageTypes = [];

    for (const contentType of contentTypes) {
      if (ContentTypeAssertions.isEntryContentType(contentType)) {
        entryContentTypes.push(contentType);
      } else if (ContentTypeAssertions.isAssetContentType(contentType)) {
        assetTypes.push(contentType);
      } else if (ContentTypeAssertions.isFormContentType(contentType)) {
        formContentTypes.push(contentType);
      } else if (ContentTypeAssertions.isWebpageContentType(contentType)) {
        webpageTypes.push(contentType);
      }
    }

    for (const contentType of entryContentTypes) {
      const fields = contentType.fields?.map((field) => {
        if (field?.dataFormat?.startsWith('component.')) {
          delete (field as any).fields;
        }

        if (field.validations?.allowedFieldTypes?.fields) {
          field.validations?.allowedFieldTypes?.fields.map(
            (innerField: Field) => {
              if (innerField?.dataFormat?.startsWith('component.')) {
                delete (innerField as any).fields;
              }
              return innerField;
            }
          );
        }
        return field;
      });

      contentType.fields = fields;
    }
    return {
      entryContentTypes,
      assetTypes,
      formContentTypes,
      webpageTypes,
    };
  };
}
